import React, { useMemo } from "react";
import {
  Card,
  Form,
  Button,
  Table,
  Row,
  Col,
  Badge,
  Alert,
  InputGroup,
} from "react-bootstrap";
import {
  FaPlus,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";

const createFaqItem = (sortOrder = 1) => ({
  id: `faq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  title: "",
  description: "",
  isActive: true,
  sortOrder,
});

function normalizeFaqs(faqsRaw) {
  const raw =
    faqsRaw && typeof faqsRaw === "object"
      ? faqsRaw
      : {};

  const enabled =
    typeof raw.enabled === "boolean" ? raw.enabled : false;

  const items = Array.isArray(raw.items)
    ? raw.items.map((item, index) => ({
        id:
          item?.id ||
          `faq_${Date.now()}_${index}_${Math.random()
            .toString(36)
            .slice(2, 6)}`,
        title: String(item?.title || "").trim(),
        description: String(item?.description || "").trim(),
        isActive:
          typeof item?.isActive === "boolean" ? item.isActive : true,
        sortOrder:
          Number.isFinite(Number(item?.sortOrder))
            ? Number(item.sortOrder)
            : index + 1,
      }))
    : [];

  items.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  return { enabled, items };
}

export default function ManageFaqs({ branding, setBranding }) {
  const faqs = useMemo(() => normalizeFaqs(branding?.faqs), [branding?.faqs]);

  const patchFaqs = (nextFaqs) => {
    setBranding((prev) => ({
      ...prev,
      faqs: nextFaqs,
    }));
  };

  const handleEnabledChange = (checked) => {
    patchFaqs({
      ...faqs,
      enabled: checked,
    });
  };

  const updateItem = (id, patch) => {
    const nextItems = faqs.items.map((item) =>
      item.id === id ? { ...item, ...patch } : item
    );
    patchFaqs({
      ...faqs,
      items: nextItems,
    });
  };

  const addFaq = () => {
    const nextItems = [
      ...faqs.items,
      createFaqItem(faqs.items.length + 1),
    ];
    patchFaqs({
      ...faqs,
      items: nextItems,
    });
  };

  const deleteFaq = (id) => {
    const nextItems = faqs.items
      .filter((item) => item.id !== id)
      .map((item, index) => ({
        ...item,
        sortOrder: index + 1,
      }));

    patchFaqs({
      ...faqs,
      items: nextItems,
    });
  };

  const toggleActive = (id) => {
    const target = faqs.items.find((item) => item.id === id);
    if (!target) return;
    updateItem(id, { isActive: !target.isActive });
  };

  const moveItem = (index, direction) => {
    const nextItems = [...faqs.items];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= nextItems.length) return;

    [nextItems[index], nextItems[targetIndex]] = [
      nextItems[targetIndex],
      nextItems[index],
    ];

    const normalized = nextItems.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1,
    }));

    patchFaqs({
      ...faqs,
      items: normalized,
    });
  };

  const activeCount = faqs.items.filter((item) => item.isActive).length;

  return (
    <div className="pt-3">
      <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Manage FAQs</h5>
              <div className="text-muted small">
                FAQs are saved inside config as <code>faqs</code>.
              </div>
            </div>

            <Button variant="primary" onClick={addFaq}>
              <FaPlus className="me-2" />
              Add FAQ
            </Button>
          </div>

          <Alert variant="info" className="mb-4">
            When enabled, FAQs load from <strong>config.faqs</strong>. You can add,
            edit, delete, activate/deactivate, and reorder items here.
          </Alert>

          <Row className="mb-4">
            <Col md={8}>
              <Form.Check
                type="switch"
                id="faq-enabled-switch"
                label="Show FAQs section"
                checked={faqs.enabled}
                onChange={(e) => handleEnabledChange(e.target.checked)}
              />
              <div className="text-muted small mt-1">
                Turn this on to render FAQs on the booking page.
              </div>
            </Col>

            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Badge bg={faqs.enabled ? "success" : "secondary"} className="me-2">
                {faqs.enabled ? "Enabled" : "Disabled"}
              </Badge>
              <Badge bg="light" text="dark">
                {activeCount} Active / {faqs.items.length} Total
              </Badge>
            </Col>
          </Row>

          {faqs.items.length === 0 ? (
            <div className="border rounded-3 p-4 text-center bg-light">
              <div className="fw-semibold mb-2">No FAQs added yet</div>
              <div className="text-muted mb-3">
                Add your first FAQ to start building the section.
              </div>
              <Button variant="outline-primary" onClick={addFaq}>
                <FaPlus className="me-2" />
                Add First FAQ
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Order</th>
                    <th style={{ minWidth: 260 }}>Title</th>
                    <th style={{ minWidth: 320 }}>Description</th>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 180 }} className="text-end">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {faqs.items.map((item, index) => (
                    <tr key={item.id}>
                      <td>
                        <InputGroup size="sm">
                          <Form.Control
                            value={item.sortOrder}
                            readOnly
                            style={{ maxWidth: 58 }}
                          />
                        </InputGroup>
                      </td>

                      <td>
                        <Form.Control
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            updateItem(item.id, { title: e.target.value })
                          }
                          placeholder="Enter FAQ title"
                        />
                      </td>

                      <td>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={item.description}
                          onChange={(e) =>
                            updateItem(item.id, {
                              description: e.target.value,
                            })
                          }
                          placeholder="Enter FAQ description"
                        />
                      </td>

                      <td>
                        <Button
                          variant={item.isActive ? "outline-success" : "outline-secondary"}
                          size="sm"
                          onClick={() => toggleActive(item.id)}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </Button>
                      </td>

                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => moveItem(index, "up")}
                            disabled={index === 0}
                            title="Move Up"
                          >
                            <FaArrowUp />
                          </Button>

                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => moveItem(index, "down")}
                            disabled={index === faqs.items.length - 1}
                            title="Move Down"
                          >
                            <FaArrowDown />
                          </Button>

                          <Button
                            variant={item.isActive ? "outline-warning" : "outline-success"}
                            size="sm"
                            onClick={() => toggleActive(item.id)}
                            title="Toggle Active"
                          >
                            {item.isActive ? <FaToggleOn /> : <FaToggleOff />}
                          </Button>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => deleteFaq(item.id)}
                            title="Delete FAQ"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}